import useSWR from "swr";
import { createFetcher } from "./fetcher";
import { Storage } from "../interfaces/storage";
import { Customer } from "../interfaces/customer";
import { APIWithPagination } from "../interfaces/pagination";
import api from "./api";
import { alertSuccess, alertError } from "../utils/alert";

export const useAllStorages = ({
  page = 0,
  pageSize = 25,
  search,
  ordering,
}: APIWithPagination & { isTenant?: boolean }) => {
  let url = `/storages/`;

  url += `?page=${page + 1}&page_size=${pageSize}`;

  if (search !== undefined) {
    url += `&search=${search}`;
  }

  if (ordering !== undefined) {
    url += `&ordering=${ordering}`;
  }

  const snapshot = useSWR<{
    count: number;
    next: string;
    previous: string;
    results: Storage[];
    total_pages: number;
  }>(url, createFetcher, {
    revalidateOnMount: true,
  });

  return { ...snapshot };
};

export const useAllCustomers = ({
  page = 0,
  pageSize = 25,
  search,
  ordering,
}: APIWithPagination) => {
  let url = `/customers/`;

  url += `?page=${page + 1}&page_size=${pageSize}`;

  if (search !== undefined) {
    url += `&search=${search}`;
  }

  if (ordering !== undefined) {
    url += `&ordering=${ordering}`;
  }

  const snapshot = useSWR<{
    count: number;
    next: string;
    previous: string;
    results: Customer[];
    total_pages: number;
  }>(url, createFetcher, {
    revalidateOnMount: true,
  });

  return { ...snapshot };
};

export const addStorages = async ({
  storages,
  onSuccess,
  setAlertInfo,
}: {
  storages: Storage[];
  onSuccess: () => void;
  setAlertInfo: (value: any) => void;
}) => {
  const url = `/storages/create`;

  try {
    await api.post(url, storages);

    alertSuccess(
      `Storage${storages.length > 1 ? "s" : ""} added!`,
      setAlertInfo
    );
    onSuccess();
  } catch (error: any) {
    alertError(
      error?.response?.status === 401
        ? "Please login first."
        : error.response.data?.[0].email?.[0] ??
            error.response.data.detail ??
            error.response.data.message ??
            error.toString(),
      setAlertInfo
    );
  }
};

export const useStorageInfo = (storageId: string | number | undefined) => {
  let url = `/storages/${storageId}`;

  const snapshot = useSWR<Storage>(
    () => {
      if (storageId === -1) return;
      return url;
    },
    createFetcher,
    {
      revalidateOnFocus: false,
    }
  );

  return { ...snapshot };
};

export const updateStorage = async ({
  storageId,
  body,
  onSuccess,
  setAlertInfo,
}: {
  storageId: Storage;
  body: Partial<Storage>;
  onSuccess: () => void;
  setAlertInfo: (value: any) => void;
}) => {
  const url = `/storages/${storageId}/update`;

  try {
    await api.patch(url, body);

    alertSuccess(`Storage "${body.name}" updated!`, setAlertInfo);
    onSuccess();
  } catch (error: any) {
    alertError(
      error?.response?.status === 401
        ? "Please login first."
        : error.response.data.detail ??
            error.response.data.message ??
            error.toString(),
      setAlertInfo
    );
  }
};

export const deleteStorage = async ({
  storage,
  onSuccess,
  setAlertInfo,
}: {
  storage: Storage;
  onSuccess: () => void;
  setAlertInfo: (value: any) => void;
}) => {
  const url = `/storages/${storage.id}/delete`;

  try {
    await api.delete(url);

    alertSuccess(`Storage deleted!`, setAlertInfo);
    onSuccess();
  } catch (error: any) {
    alertError(
      error?.response?.status === 401
        ? "Please login first."
        : error.response.data.detail ??
            error.response.data.message ??
            error.toString(),
      setAlertInfo
    );
  }
};